import React from 'react';
import {
    Box, TextField, Typography, Divider, IconButton,
    FormControl, InputLabel, Select, MenuItem, FormHelperText,
    CircularProgress, Autocomplete,
} from '@mui/material';
import { Remove } from '@mui/icons-material';
import { FlexBetween } from '../FlexBetween';

export interface ProductPlanPanelProps {
    index: number;
    plan: Record<string, any>;
    onFieldChange: (index: number, field: string, value: any) => void;
    onQuotaChange: (index: number, field: string, value: any, subField?: string) => void;
    onRemove: (index: number) => void;
    licenseTypes: string[];
    modules: string[];
    modulesLoading?: boolean;
    modulesError?: string | null;
    errors?: Record<string, string>;
    variant: 'company' | 'businessPlan';
    workerPoolNames?: string[];
    workerPoolNamesLoading?: boolean;
    workerPoolNamesError?: string | null;
}

const LICENSE_TYPES_FALLBACK = ['Enterprise', 'BusinessPlus', 'Business', 'VerifiedPro', 'PublicFree'];

export const ProductPlanPanel: React.FC<ProductPlanPanelProps> = ({
    index,
    plan,
    onFieldChange,
    onQuotaChange,
    onRemove,
    licenseTypes,
    modules,
    modulesLoading = false,
    modulesError = null,
    errors = {},
    variant,
    workerPoolNames = [],
    workerPoolNamesLoading = false,
    workerPoolNamesError = null,
}) => {
    const effectiveLicenseTypes = licenseTypes.length > 0 ? licenseTypes : LICENSE_TYPES_FALLBACK;

    const getQuotaValue = (field: string, subField: string = 'perDayLimit'): string => {
        if (variant === 'company') {
            const quota = plan[field];
            return quota?.[subField] != null ? String(quota[subField]) : '';
        }
        const val = plan[field];
        return val != null && val !== 0 ? String(val) : '';
    };

    const handleQuotaFieldChange = (field: string, rawValue: string, subField: string = 'perDayLimit') => {
        const numVal = rawValue === '' ? undefined : parseInt(rawValue, 10);
        onQuotaChange(index, field, numVal, subField);
    };

    const isInstantUpdate = plan.module === 'instantupdate.ai';
    const isAutomateTest = plan.module === 'automatetest.ai';
    const isAnamnesisCloud = plan.module === 'anamnesis.cloud';
    const isAssetsManagement = plan.module === 'assetsmanagement.ai';
    const isBeamonScreen = plan.module === 'beamonscreen.com';

    const beamonScreenFields: { label: string; field: string; helper: string }[] = [
        { label: 'Total Number of screens *', field: 'totalScreens', helper: 'Max screens allowed for this company plan' },
        { label: 'Total Number of Reels *', field: 'totalReels', helper: 'Max Reels allowed for this company plan' },
        { label: 'Total Number of Playlists *', field: 'totalPlaylists', helper: 'Max playlists allowed for this company plan' },
        { label: 'Cloud Space Size *', field: 'cloudSpaceInSizeInGB', helper: 'Max cloud space size allowed for this company plan (GB)' },
    ];

    const quotaFields: { label: string; field: string }[] = variant === 'company'
        ? [
            { label: 'WhatsApp Messages / Day', field: 'whatsApp' },
            { label: 'WhatsApp Templates / Day', field: 'whatsAppTemplate' },
            { label: 'WA Business Messages / Day', field: 'whatsAppBusiness' },
            { label: 'WA Business Templates / Day', field: 'whatsAppBusinessTemplate' },
            { label: 'GSM Text Messages / Day', field: 'gsmText' },
            { label: 'GSM Text Templates / Day', field: 'gsmTextTemplate' },
            { label: 'Telegram Messages / Day', field: 'telegram' },
            { label: 'Telegram Templates / Day', field: 'telegramTemplate' },
            { label: 'Emails / Day', field: 'email' },
            { label: 'Email Templates / Day', field: 'emailTemplate' },
        ]
        : [
            { label: 'WhatsApp Messages / Day', field: 'whatsAppPerDay' },
            { label: 'WhatsApp Total Credit', field: 'whatsAppTotalCredit' },
            { label: 'WhatsApp Templates / Day', field: 'whatsAppTemplatePerDay' },
            { label: 'WhatsApp Template Total Credit', field: 'whatsAppTemplateTotalCredit' },
            { label: 'WA Business Messages / Day', field: 'whatsAppBusinessPerDay' },
            { label: 'WA Business Total Credit', field: 'whatsAppBusinessTotalCredit' },
            { label: 'WA Business Templates / Day', field: 'whatsAppBusinessTemplatePerDay' },
            { label: 'WA Business Template Total Credit', field: 'whatsAppBusinessTemplateTotalCredit' },
            { label: 'GSM Text Messages / Day', field: 'gsmTextPerDay' },
            { label: 'GSM Text Total Credit', field: 'gsmTextTotalCredit' },
            { label: 'GSM Text Templates / Day', field: 'gsmTextTemplatePerDay' },
            { label: 'GSM Text Template Total Credit', field: 'gsmTextTemplateTotalCredit' },
            { label: 'Telegram Messages / Day', field: 'telegramPerDay' },
            { label: 'Telegram Total Credit', field: 'telegramTotalCredit' },
            { label: 'Telegram Templates / Day', field: 'telegramTemplatePerDay' },
            { label: 'Telegram Template Total Credit', field: 'telegramTemplateTotalCredit' },
            { label: 'Emails / Day', field: 'emailPerDay' },
            { label: 'Email Total Credit', field: 'emailTotalCredit' },
            { label: 'Email Templates / Day', field: 'emailTemplatePerDay' },
            { label: 'Email Template Total Credit', field: 'emailTemplateTotalCredit' },
        ];

    return (
        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, mb: 2 }}>
            <FlexBetween sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Plan {index + 1}</Typography>
                <IconButton onClick={() => onRemove(index)} color="error" size="small">
                    <Remove />
                </IconButton>
            </FlexBetween>

            {/* Row 1: License Type, Module, Number of Users */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl sx={{ flex: 1 }} error={!!errors[`productPlans.${index}.licenseType`]}>
                    <InputLabel>License Type *</InputLabel>
                    <Select
                        value={plan.licenseType || ''}
                        label="License Type *"
                        onChange={(e) => onFieldChange(index, 'licenseType', e.target.value)}
                    >
                        {effectiveLicenseTypes.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                    {errors[`productPlans.${index}.licenseType`] && (
                        <FormHelperText sx={{ color: '#f44336' }}>
                            {errors[`productPlans.${index}.licenseType`]}
                        </FormHelperText>
                    )}
                </FormControl>

                <FormControl sx={{ flex: 1 }} error={!!errors[`productPlans.${index}.module`]}>
                    <InputLabel>Module *</InputLabel>
                    <Select
                        value={plan.module || ''}
                        label="Module *"
                        onChange={(e) => onFieldChange(index, 'module', e.target.value)}
                        disabled={modulesLoading}
                    >
                        {modulesLoading ? (
                            <MenuItem disabled>
                                <CircularProgress size={20} sx={{ mr: 1 }} /> Loading modules...
                            </MenuItem>
                        ) : modulesError ? (
                            <MenuItem disabled>Error loading modules</MenuItem>
                        ) : (
                            modules.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))
                        )}
                    </Select>
                    {errors[`productPlans.${index}.module`] && (
                        <FormHelperText sx={{ color: '#f44336' }}>
                            {errors[`productPlans.${index}.module`]}
                        </FormHelperText>
                    )}
                    {modulesLoading && <FormHelperText sx={{ color: 'info.main' }}>Loading available modules...</FormHelperText>}
                    {modulesError && <FormHelperText sx={{ color: '#f44336' }}>{modulesError}</FormHelperText>}
                </FormControl>

                <TextField
                    label="Number of Users *"
                    type="number"
                    value={plan.noOfUsers || ''}
                    onChange={(e) => onFieldChange(index, 'noOfUsers', parseInt(e.target.value) || 0)}
                    sx={{ flex: 1 }}
                    error={!!errors[`productPlans.${index}.noOfUsers`]}
                    helperText={errors[`productPlans.${index}.noOfUsers`]}
                />
            </Box>

            {/* Row 2: Date fields (company) or Duration (businessPlan) */}
            {variant === 'company' && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Started At *"
                        type="date"
                        value={plan.startedAt || ''}
                        onChange={(e) => onFieldChange(index, 'startedAt', e.target.value)}
                        sx={{ flex: 1 }}
                        error={!!errors[`productPlans.${index}.startedAt`]}
                        helperText={errors[`productPlans.${index}.startedAt`]}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Ends At *"
                        type="date"
                        value={plan.endsAt || ''}
                        onChange={(e) => onFieldChange(index, 'endsAt', e.target.value)}
                        sx={{ flex: 1 }}
                        error={!!errors[`productPlans.${index}.endsAt`]}
                        helperText={errors[`productPlans.${index}.endsAt`]}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
            )}

            {variant === 'businessPlan' && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Duration (months) *"
                        type="number"
                        value={plan.durationMonths || ''}
                        onChange={(e) => onFieldChange(index, 'durationMonths', parseInt(e.target.value) || 0)}
                        sx={{ flex: 1, maxWidth: 250 }}
                        error={!!errors[`productPlans.${index}.durationMonths`]}
                        helperText={errors[`productPlans.${index}.durationMonths`]}
                    />
                </Box>
            )}

            {/* Conditional field for anamnesis.cloud */}
            {isAnamnesisCloud && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <TextField
                        label="Total Number of Git Repo allowed"
                        type="number"
                        value={plan.totalGitRepos ?? ''}
                        onChange={(e) => onFieldChange(index, 'totalGitRepos', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                        sx={{ flex: 1, maxWidth: 320 }}
                        helperText="Maximum git repositories the company can connect."
                    />
                </Box>
            )}

            {/* Conditional field for assetsmanagement.ai */}
            {isAssetsManagement && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <TextField
                        label="Cloud Storage Size (GB) *"
                        type="number"
                        value={plan.totalSizeInPlan ?? ''}
                        onChange={(e) => onFieldChange(index, 'totalSizeInPlan', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                        sx={{ flex: 1, maxWidth: 320 }}
                        error={!!errors[`productPlans.${index}.totalSizeInPlan`]}
                        helperText={errors[`productPlans.${index}.totalSizeInPlan`] || 'Max cloud storage allowed for this company plan (GB)'}
                        inputProps={{ min: 0, step: 1 }}
                    />
                </Box>
            )}

            {/* Conditional fields for beamonscreen.com */}
            {isBeamonScreen && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    {beamonScreenFields.map((bf) => (
                        <TextField
                            key={bf.field}
                            label={bf.label}
                            type="number"
                            value={plan[bf.field] ?? ''}
                            onChange={(e) => onFieldChange(index, bf.field, e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                            sx={{ flex: 1, minWidth: 220 }}
                            error={!!errors[`productPlans.${index}.${bf.field}`]}
                            helperText={errors[`productPlans.${index}.${bf.field}`] || bf.helper}
                        />
                    ))}
                </Box>
            )}

            {/* Conditional quota fields for instantupdate.ai */}
            {isInstantUpdate && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Instant Update Quotas {variant === 'company' ? '(Leave blank for UNLIMITED usage)' : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {quotaFields.map((qf) => (
                            <React.Fragment key={qf.field}>
                                <TextField
                                    label={qf.label}
                                    type="number"
                                    value={getQuotaValue(qf.field)}
                                    onChange={(e) => handleQuotaFieldChange(qf.field, e.target.value)}
                                    sx={{ flex: 1, minWidth: 220 }}
                                />
                                {variant === 'company' && (
                                    <TextField
                                        label={`${qf.label.replace(' / Day', '')} Total Credit`}
                                        type="number"
                                        value={getQuotaValue(qf.field, 'totalCredit')}
                                        onChange={(e) => handleQuotaFieldChange(qf.field, e.target.value, 'totalCredit')}
                                        sx={{ flex: 1, minWidth: 220 }}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                        <Autocomplete
                            options={workerPoolNames}
                            loading={workerPoolNamesLoading}
                            value={plan.gsmWorkerName || null}
                            onChange={(_, newValue) => onFieldChange(index, 'gsmWorkerName', (newValue as string) || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Worker Pool Name"
                                    sx={{ flex: 1, minWidth: 220 }}
                                    helperText={workerPoolNamesError ? `Error: ${workerPoolNamesError}` : 'Optional. Select or clear.'}
                                    error={!!workerPoolNamesError}
                                />
                            )}
                            sx={{ flex: 1, minWidth: 220 }}
                            autoHighlight
                        />
                        <TextField
                            label="Sender Header Name"
                            value={plan.senderHeaderName ?? ''}
                            onChange={(e) => onFieldChange(index, 'senderHeaderName', e.target.value)}
                            placeholder="640807"
                            sx={{ flex: 1, minWidth: 220 }}
                            helperText="Optional. Default: 640807"
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default ProductPlanPanel;
